import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FilterIcon,
  XIcon,
  LocationMarkerIcon,
  ClockIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  PlusCircleIcon,
  DesktopComputerIcon,
  ViewGridIcon,
  ViewListIcon,
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ShoppingCartIcon,
  CheckCircleIcon,
  XCircleIcon,
  SearchIcon,
  EyeIcon,
  AdjustmentsIcon
} from '@heroicons/react/outline';

// Importation des composants (pour simplifier, ils sont inclus dans ce fichier)
// En production, ils seraient importés depuis leurs fichiers respectifs
import NetworkIntroSection from '../composant/NetworkIntroSection';
import FilterSidebar from '../composant/FilterSidebar';
import Navbar from '../composant/Navbar';

// Données simulées pour les sites
const sitesData = [
  {
    id: 1,
    name: "Centre Commercial Douala Grand Mall",
    type: "Centre Commercial",
    location: "Douala, Akwa",
    screens: 4,
    screenType: "indoor",
    pricePerDay: 45000,
    views: 15000,
    availability: true,
    trafficType: "Élevé",
    peakHours: "14h - 20h",
    avgAge: "18-45 ans",
    imageUrl: "/assets/site_carrefour1.jpg"
  },
  {
    id: 2,
    name: "Hôtel Akwa Palace",
    type: "Hôtel",
    location: "Douala, Bonanjo",
    screens: 2,
    screenType: "indoor",
    pricePerDay: 35000,
    views: 5000,
    availability: true,
    trafficType: "Moyen - Haut de gamme",
    peakHours: "8h - 10h, 18h - 22h",
    avgAge: "30-55 ans",
    imageUrl: "/assets/site_carrefour2.jpg"
  },
  {
    id: 3,
    name: "Restaurant La Fourchette",
    type: "Restaurant",
    location: "Douala, Bonapriso",
    screens: 1,
    screenType: "indoor",
    pricePerDay: 20000,
    views: 800,
    availability: false,
    trafficType: "Moyen - Premium",
    peakHours: "12h - 14h, 19h - 23h",
    avgAge: "25-50 ans",
    imageUrl: "/assets/site_carrefour3.jpg"
  },
  {
    id: 4,
    name: "Agence de Voyage Sky Tours",
    type: "Agence de Voyage",
    location: "Douala, Bali",
    screens: 3,
    screenType: "indoor",
    pricePerDay: 30000,
    views: 1200,
    availability: true,
    trafficType: "Ciblé",
    peakHours: "9h - 17h",
    avgAge: "30-60 ans",
    imageUrl: "/assets/site_3.jpg"
  },
  {
    id: 5,
    name: "Boulevard de la Liberté",
    type: "Espace Public",
    location: "Douala, Centre",
    screens: 2,
    screenType: "outdoor",
    pricePerDay: 55000,
    views: 25000,
    availability: true,
    trafficType: "Très élevé",
    peakHours: "7h - 20h",
    avgAge: "Tous âges",
    imageUrl: "/assets/site_4.jpg"
  },
  {
    id: 6,
    name: "Sapeur Plaza",
    type: "Centre Commercial",
    location: "Yaoundé, Centre",
    screens: 6,
    screenType: "indoor",
    pricePerDay: 40000,
    views: 12000,
    availability: true,
    trafficType: "Élevé",
    peakHours: "10h - 19h",
    avgAge: "20-45 ans",
    imageUrl: "/assets/site_5.jpg"
  },
  {
    id: 7,
    name: "Université de Yaoundé I",
    type: "Université",
    location: "Yaoundé, Ngoa-Ekellé",
    screens: 3,
    screenType: "indoor",
    pricePerDay: 25000,
    views: 8000,
    availability: false,
    trafficType: "Jeune - Éducatif",
    peakHours: "8h - 16h",
    avgAge: "18-25 ans",
    imageUrl: "/assets/site_12.jpg"
  },
  {
    id: 8,
    name: "Casino Supermarché",
    type: "Supermarché",
    location: "Douala, Bonanjo",
    screens: 2,
    screenType: "indoor",
    pricePerDay: 38000,
    views: 7500,
    availability: true,
    trafficType: "Moyen - Régulier",
    peakHours: "10h - 19h",
    avgAge: "25-55 ans",
    imageUrl: "/assets/site_carrefour2.jpg"
  }
];

// Données simulées pour les packages
const packagesData = [
  {
    id: 1,
    name: "Retail Network",
    subtitle: "Supermarché & Centres commerciaux",
    description: "Un réseau premium couvrant les principaux centres commerciaux et supermarchés de la ville, touchant une clientèle diversifiée pendant ses moments d'achat.",
    price: 350000,
    duration: "7 jours",
    siteCount: 12,
    screenCount: 24,
    estimatedViews: 85000,
    featured: false,
    color: "#E84E0E",
    lightColor: "#FFF4F0",
    locations: ["Douala Centre", "Akwa", "Bonanjo"],
    siteTypes: ["Supermarchés", "Centres commerciaux", "Boutiques premium"],
    benefits: [
      "Présence dans 12 sites commerciaux stratégiques",
      "24 écrans LCD HD en intérieur",
      "Audience clientèle premium en situation d'achat",
      "Ciblage durant les heures d'affluence",
      "Visibilité optimale près des caisses et entrées",
      "Rapport de performance détaillé"
    ],
    imageUrl: "/assets/site_carrefour1.jpg",
    availability: true
  },
  {
    id: 2,
    name: "Business District",
    subtitle: "Quartiers d'affaires & Entreprises",
    description: "Ciblez les professionnels et décideurs dans leurs environnements de travail avec notre réseau d'écrans présents dans les halls d'immeubles de bureaux et zones d'affaires.",
    price: 450000,
    duration: "14 jours",
    siteCount: 15,
    screenCount: 30,
    estimatedViews: 70000,
    featured: true,
    color: "#8B5CF6",
    lightColor: "#F5F3FF",
    locations: ["Bonanjo", "Bonapriso", "Akwa"],
    siteTypes: ["Tours de bureaux", "Sièges sociaux", "Centres d'affaires"],
    benefits: [
      "15 sites premium dans les quartiers d'affaires",
      "30 écrans stratégiquement placés dans les lobbies et ascenseurs",
      "Visibilité auprès des cadres et décideurs",
      "Diffusion en heures de bureau (7h-19h)",
      "Ciblage sectoriel possible par zones d'activité",
      "Analyse démographique des impressions"
    ],
    imageUrl: "/assets/site_4.jpg",
    availability: true
  },
  {
    id: 3,
    name: "Lifestyle & Leisure",
    subtitle: "Restaurants, Cafés & Lieux de détente",
    description: "Touchez une clientèle détendue et réceptive dans les restaurants, cafés et espaces de loisirs premium de la ville avec une présence visuelle élégante.",
    price: 280000,
    duration: "10 jours",
    siteCount: 18,
    screenCount: 25,
    estimatedViews: 60000,
    featured: false,
    color: "#10B981",
    lightColor: "#ECFDF5",
    locations: ["Akwa", "Bonapriso", "Bali", "Deido"],
    siteTypes: ["Restaurants haut de gamme", "Cafés branchés", "Bars lounge", "Espaces détente"],
    benefits: [
      "18 établissements premium sélectionnés",
      "25 écrans discrets intégrés aux décors",
      "Audience captive et détendue",
      "Programmation intelligente selon les heures de repas",
      "Association à des environnements agréables",
      "Format créatif adapté à l'ambiance des lieux"
    ],
    imageUrl: "/assets/site_8.jpg",
    availability: false
  },
  {
    id: 4,
    name: "Knowledge Network",
    subtitle: "Universités, Campus & Centres de formation",
    description: "Connectez-vous avec la jeunesse étudiante et les apprenants dans les établissements d'enseignement et centres de formation professionnelle.",
    price: 320000,
    duration: "14 jours",
    siteCount: 10,
    screenCount: 22,
    estimatedViews: 45000,
    featured: false,
    color: "#F59E0B",
    lightColor: "#FEF3C7",
    locations: ["Campus universitaires", "Écoles supérieures", "Centres de formation"],
    siteTypes: ["Universités", "Écoles d'ingénieurs", "Business schools", "Centres de certification"],
    benefits: [
      "10 établissements éducatifs prestigieux",
      "22 écrans dans les halls, cafétérias et bibliothèques",
      "Audience jeune, connectée et en formation",
      "Diffusion adaptée au calendrier académique",
      "Affinité naturelle avec l'innovation et la technologie",
      "Environnement propice à l'attention"
    ],
    imageUrl: "/assets/site_11.jpg",
    availability: true
  }
];

// Composant principal DigitalNetworkPage
const DigitalNetworkPage = () => {
  // États pour les filtres et la visualisation
  const [filters, setFilters] = useState({
    activeView: 'sites', // 'sites' ou 'packages'
    priceRange: [5000, 100000],
    selectedCities: [],
    selectedSiteTypes: [],
    selectedPackageTypes: [],
    screenType: 'all',
    availabilityFilter: 'all',
    minScreens: 1,
    searchQuery: '',
    displayMode: 'grid'
  });
  
  const [displayMode, setDisplayMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedItems, setSelectedItems] = useState([]);
  const [campaignPeriod, setCampaignPeriod] = useState({
    startDate: new Date(),
    endDate: new Date(new Date().setDate(new Date().getDate() + 7))
  });
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Filtrer les sites et packages selon les critères
  const filteredSites = sitesData.filter(site => {
    // Filtre par prix
    if (site.pricePerDay < filters.priceRange[0] || site.pricePerDay > filters.priceRange[1]) return false;
    
    // Filtre par ville
    if (filters.selectedCities.length > 0) {
      const locationCity = site.location.split(',')[0].trim().toLowerCase();
      if (!filters.selectedCities.some(city => locationCity.includes(city.toLowerCase()))) return false;
    }
    
    // Filtre par type de site
    if (filters.selectedSiteTypes.length > 0) {
      const siteType = site.type.toLowerCase();
      if (!filters.selectedSiteTypes.some(type => siteType.includes(type.toLowerCase()))) return false;
    }
    
    // Filtre par type d'écran
    if (filters.screenType !== 'all' && site.screenType !== filters.screenType) return false;
    
    // Filtre par disponibilité
    if (filters.availabilityFilter !== 'all') {
      if (filters.availabilityFilter === 'available' && !site.availability) return false;
      if (filters.availabilityFilter === 'reserved' && site.availability) return false;
    }
    
    // Filtre par nombre d'écrans minimum
    if (site.screens < filters.minScreens) return false;
    
    // Filtre par recherche
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      if (!site.name.toLowerCase().includes(query) && 
          !site.location.toLowerCase().includes(query) &&
          !site.type.toLowerCase().includes(query)) return false;
    }
    
    return true;
  });
  
  const filteredPackages = packagesData.filter(pkg => {
    // Filtre par prix
    if (pkg.price < filters.priceRange[0] || pkg.price > filters.priceRange[1]) return false;
    
    // Filtre par type de package
    if (filters.selectedPackageTypes.length > 0) {
      const packageName = pkg.name.toLowerCase();
      if (!filters.selectedPackageTypes.some(type => packageName.includes(type.toLowerCase()))) return false;
    }
    
    // Filtre par disponibilité
    if (filters.availabilityFilter !== 'all') {
      if (filters.availabilityFilter === 'available' && !pkg.availability) return false;
      if (filters.availabilityFilter === 'reserved' && pkg.availability) return false;
    }
    
    // Filtre par recherche
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      if (!pkg.name.toLowerCase().includes(query) && 
          !pkg.subtitle.toLowerCase().includes(query)) return false;
    }
    
    return true;
  });
  
  // Calculer le coût total des éléments sélectionnés
  const calculateTotalCost = () => {
    if (selectedItems.length === 0) return 0;
    
    let totalCost = 0;
    const daysDiff = Math.ceil((campaignPeriod.endDate - campaignPeriod.startDate) / (1000 * 60 * 60 * 24));
    
    selectedItems.forEach(item => {
      if (item.type === 'site') {
        const site = sitesData.find(s => s.id === item.id);
        if (site) totalCost += site.pricePerDay * daysDiff;
      } else if (item.type === 'package') {
        const pkg = packagesData.find(p => p.id === item.id);
        if (pkg) totalCost += pkg.price;
      }
    });
    
    return totalCost;
  };
  
  // Gestionnaire pour ajouter/supprimer des éléments du panier
  const toggleItemSelection = (id, type) => {
    // Vérifier si l'élément est déjà sélectionné
    const isAlreadySelected = selectedItems.some(item => item.id === id && item.type === type);
    
    if (isAlreadySelected) {
      // Supprimer l'élément
      setSelectedItems(prev => prev.filter(item => !(item.id === id && item.type === type)));
    } else {
      // Ajouter l'élément
      setSelectedItems(prev => [...prev, { id, type }]);
    }
  };
  
  // Gérer le changement de période de campagne
  const handlePeriodChange = (startOrEnd, newDate) => {
    if (startOrEnd === 'start') {
      setCampaignPeriod(prev => ({ ...prev, startDate: newDate }));
    } else {
      setCampaignPeriod(prev => ({ ...prev, endDate: newDate }));
    }
  };
  
  // Détecter si l'écran est mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Vérifier à l'initialisation
    checkIfMobile();
    
    // Ajouter l'écouteur d'événement
    window.addEventListener('resize', checkIfMobile);
    
    // Nettoyer l'écouteur
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);
  
  // Gestionnaire pour les changements de filtre
  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    // Réinitialiser la page lors d'un changement de filtre
    setCurrentPage(1);
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Section de la navbar */}
      <Navbar />
      {/* Section d'introduction */}
      <NetworkIntroSection />
      
      {/* Panier flottant réduit */}
      {selectedItems.length > 0 && !isCartOpen && (
        <motion.button
          className="fixed bottom-6 right-6 z-40 bg-[#E84E0E] text-white p-4 rounded-full shadow-lg"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsCartOpen(true)}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
        >
          <div className="relative">
            <ShoppingCartIcon className="h-6 w-6" />
            <span className="absolute -top-2 -right-2 w-5 h-5 bg-white text-[#E84E0E] rounded-full flex items-center justify-center text-xs font-bold">
              {selectedItems.length}
            </span>
          </div>
        </motion.button>
      )}
      
      {/* Section principale avec filtres et contenu */}
      <section className="container mx-auto px-4 py-12 relative" id="explorer">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {/* Zone de filtres (côté gauche) */}
          <div className="md:col-span-3">
            <FilterSidebar 
              onFilterChange={handleFilterChange}
              viewMode={filters.activeView}
              setViewMode={(mode) => handleFilterChange({ activeView: mode })}
            />
          </div>
          
          {/* Zone de contenu principal (côté droit) */}
          <div className="md:col-span-9">
            {/* Contenu conditionnel selon le mode d'affichage */}
            {filters.activeView === 'sites' ? (
              <SiteList 
                sites={filteredSites} 
                displayMode={filters.displayMode}
                selectedItems={selectedItems}
                onToggleSelection={(id) => toggleItemSelection(id, 'site')}
                onPageChange={setCurrentPage}
                currentPage={currentPage}
              />
            ) : (
              <PackageList 
                packages={filteredPackages}
                displayMode={filters.displayMode}
                selectedItems={selectedItems}
                onToggleSelection={(id) => toggleItemSelection(id, 'package')}
                onPageChange={setCurrentPage}
                currentPage={currentPage}
              />
            )}
          </div>
        </div>
      </section>
      
      {/* Section de sélection de période et calcul du coût */}
      {selectedItems.length > 0 && (
        <motion.section 
          className="bg-white shadow-xl border-t border-gray-200 py-8 sticky bottom-0 left-0 right-0 z-30"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
              {/* Sélection de période */}
              <div className="md:col-span-5">
                <CampaignPeriodSelector 
                  startDate={campaignPeriod.startDate}
                  endDate={campaignPeriod.endDate}
                  onPeriodChange={handlePeriodChange}
                />
              </div>
              
              {/* Résumé des coûts */}
              <div className="md:col-span-4">
                <CostEstimator 
                  selectedItems={selectedItems}
                  campaignPeriod={campaignPeriod}
                  totalCost={calculateTotalCost()}
                />
              </div>
              
              {/* Bouton d'action */}
              <div className="md:col-span-3 flex items-center justify-end">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-[#E84E0E] hover:bg-[#d03a09] text-white py-3 px-6 rounded-xl shadow-md flex items-center justify-center w-full md:w-auto"
                  onClick={() => setIsCartOpen(true)}
                >
                  <ShoppingCartIcon className="h-5 w-5 mr-2" />
                  <span>Voir la sélection ({selectedItems.length})</span>
                </motion.button>
              </div>
            </div>
          </div>
        </motion.section>
      )}
      
      {/* Panier détaillé */}
      <AnimatePresence>
        {isCartOpen && (
          <CartSummary 
            selectedItems={selectedItems}
            onToggleSelection={toggleItemSelection}
            campaignPeriod={campaignPeriod}
            onPeriodChange={handlePeriodChange}
            totalCost={calculateTotalCost()}
            onClose={() => setIsCartOpen(false)}
            sitesData={sitesData}
            packagesData={packagesData}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Sous-composants

// SiteList - Affichage des sites en grille ou liste
const SiteList = ({ sites, displayMode, selectedItems, onToggleSelection, onPageChange, currentPage }) => {
  const sitesPerPage = 6;
  const totalPages = Math.ceil(sites.length / sitesPerPage);
  
  // Récupérer les sites pour la page actuelle
  const currentSites = sites.slice((currentPage - 1) * sitesPerPage, currentPage * sitesPerPage);
  
  return (
    <div>
      {sites.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl shadow">
          <SearchIcon className="h-16 w-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-medium text-gray-700 mb-2">Aucun site ne correspond à vos critères</h3>
          <p className="text-gray-500">Essayez de modifier vos filtres pour trouver des sites</p>
        </div>
      ) : (
        <>
          <div className={displayMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
            {currentSites.map(site => (
              <SiteCard 
                key={site.id}
                site={site}
                isSelected={selectedItems.some(item => item.id === site.id && item.type === 'site')}
                onToggleSelection={() => onToggleSelection(site.id)}
                displayMode={displayMode}
              />
            ))}
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination 
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
            />
          )}
        </>
      )}
    </div>
  );
};

// SiteCard - Carte individuelle pour chaque site
const SiteCard = ({ site, isSelected, onToggleSelection, displayMode }) => {
  return (
    <motion.div
      className={`bg-white shadow rounded-xl overflow-hidden border ${isSelected ? 'border-[#E84E0E]' : 'border-transparent'} transition-all`}
      whileHover={{ y: -5, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)" }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      {displayMode === 'grid' ? (
        // Affichage en grille
        <>
          <div className="relative h-48">
            <img 
              src={site.imageUrl || "https://via.placeholder.com/400x200"} 
              alt={site.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
            
            {/* Badge de disponibilité */}
            <div className="absolute top-4 right-4">
              {site.availability ? (
                <span className="bg-green-500 text-white text-xs py-1 px-3 rounded-full flex items-center">
                  <CheckCircleIcon className="h-3 w-3 mr-1" />
                  Disponible
                </span>
              ) : (
                <span className="bg-red-500 text-white text-xs py-1 px-3 rounded-full flex items-center">
                  <XCircleIcon className="h-3 w-3 mr-1" />
                  Réservé
                </span>
              )}
            </div>
            
            {/* Type de site */}
            <div className="absolute top-4 left-4">
              <span className="bg-white/20 backdrop-blur-sm text-white text-xs py-1 px-3 rounded-full">
                {site.type}
              </span>
            </div>
            
            {/* Nom et localisation */}
            <div className="absolute bottom-0 left-0 p-4 w-full">
              <h3 className="text-lg font-bold text-white mb-1 truncate">{site.name}</h3>
              <div className="flex items-center text-white/80 text-sm">
                <LocationMarkerIcon className="h-4 w-4 mr-1" />
                <span className="truncate">{site.location}</span>
              </div>
            </div>
          </div>
          
          <div className="p-4">
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-gray-50 p-2 rounded">
                <span className="block text-xs text-gray-500">Prix/jour</span>
                <span className="text-lg font-bold text-[#E84E0E]">{site.pricePerDay.toLocaleString()} FCFA</span>
              </div>
              <div className="bg-gray-50 p-2 rounded">
                <span className="block text-xs text-gray-500">Écrans</span>
                <div className="flex items-center">
                  <span className="text-lg font-bold text-gray-800 mr-2">{site.screens}</span>
                  <span className="text-xs py-1 px-2 bg-gray-200 rounded-full">
                    {site.screenType === 'indoor' ? 'Indoor' : 'Outdoor'}
                  </span>
                </div>
              </div>
              <div className="bg-gray-50 p-2 rounded">
                <span className="block text-xs text-gray-500">Audience</span>
                <span className="text-lg font-bold text-gray-800">{site.views.toLocaleString()}/jour</span>
              </div>
              <div className="bg-gray-50 p-2 rounded">
                <span className="block text-xs text-gray-500">Trafic</span>
                <span className="text-lg font-bold text-gray-800">{site.trafficType}</span>
              </div>
            </div>
            
            <div className="flex justify-between items-center">
              <button 
                className="text-gray-600 hover:text-[#E84E0E] transition-colors flex items-center"
                onClick={() => {}} // Pour afficher les détails
              >
                <EyeIcon className="h-4 w-4 mr-1" />
                <span className="text-sm">Détails</span>
              </button>
              
              <motion.button
                className={`flex items-center justify-center p-2 rounded-lg ${
                  isSelected 
                    ? 'bg-[#E84E0E] text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onToggleSelection}
              >
                {isSelected ? (
                  <CheckCircleIcon className="h-5 w-5" />
                ) : (
                  <PlusCircleIcon className="h-5 w-5" />
                )}
              </motion.button>
            </div>
          </div>
        </>
      ) : (
        // Affichage en liste
        <div className="flex flex-col md:flex-row">
          <div className="relative md:w-1/3 h-40 md:h-auto">
            <img 
              src={site.imageUrl || "https://via.placeholder.com/400x200"} 
              alt={site.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent md:bg-gradient-to-t md:from-black/70 md:via-black/30 md:to-transparent"></div>
            
            {/* Badge de disponibilité */}
            <div className="absolute top-4 right-4">
              {site.availability ? (
                <span className="bg-green-500 text-white text-xs py-1 px-3 rounded-full flex items-center">
                  <CheckCircleIcon className="h-3 w-3 mr-1" />
                  Disponible
                </span>
              ) : (
                <span className="bg-red-500 text-white text-xs py-1 px-3 rounded-full flex items-center">
                  <XCircleIcon className="h-3 w-3 mr-1" />
                  Réservé
                </span>
              )}
            </div>
            
            {/* Type de site */}
            <div className="absolute top-4 left-4">
              <span className="bg-white/20 backdrop-blur-sm text-white text-xs py-1 px-3 rounded-full">
                {site.type}
              </span>
            </div>
          </div>
          
          <div className="p-4 md:w-2/3 flex flex-col justify-between">
            <div>
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-lg font-bold text-gray-800">{site.name}</h3>
                <span className="text-xl font-bold text-[#E84E0E]">{site.pricePerDay.toLocaleString()} FCFA<span className="text-sm text-gray-500">/jour</span></span>
              </div>
              
              <div className="flex items-center text-gray-600 text-sm mb-3">
                <LocationMarkerIcon className="h-4 w-4 mr-1" />
                <span>{site.location}</span>
              </div>
              
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="flex items-center">
                  <div className="bg-gray-100 p-2 rounded-full mr-2">
                    <DesktopComputerIcon className="h-4 w-4 text-gray-600" />
                  </div>
                  <div>
                    <span className="block text-xs text-gray-500">Écrans</span>
                    <span className="text-sm font-medium text-gray-800">{site.screens} {site.screenType === 'indoor' ? '(Indoor)' : '(Outdoor)'}</span>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="bg-gray-100 p-2 rounded-full mr-2">
                    <ChartBarIcon className="h-4 w-4 text-gray-600" />
                  </div>
                  <div>
                    <span className="block text-xs text-gray-500">Audience</span>
                    <span className="text-sm font-medium text-gray-800">{site.views.toLocaleString()}/jour</span>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="bg-gray-100 p-2 rounded-full mr-2">
                    <ClockIcon className="h-4 w-4 text-gray-600" />
                  </div>
                  <div>
                    <span className="block text-xs text-gray-500">Heures de pointe</span>
                    <span className="text-sm font-medium text-gray-800">{site.peakHours}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-between items-center mt-2">
              <button 
                className="text-gray-600 hover:text-[#E84E0E] transition-colors flex items-center"
                onClick={() => {}} // Pour afficher les détails
              >
                <EyeIcon className="h-4 w-4 mr-1" />
                <span className="text-sm">Voir détails</span>
              </button>
              
              <motion.button
                className={`flex items-center justify-center p-2 rounded-lg ${
                  isSelected 
                    ? 'bg-[#E84E0E] text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onToggleSelection}
              >
                {isSelected ? (
                  <span className="flex items-center">
                    <CheckCircleIcon className="h-5 w-5 mr-1" />
                    Sélectionné
                  </span>
                ) : (
                  <span className="flex items-center">
                    <PlusCircleIcon className="h-5 w-5 mr-1" />
                    Sélectionner
                  </span>
                )}
              </motion.button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

// PackageList - Affichage des packages
const PackageList = ({ packages, displayMode, selectedItems, onToggleSelection, onPageChange, currentPage }) => {
  const packagesPerPage = 6;
  const totalPages = Math.ceil(packages.length / packagesPerPage);
  
  // Récupérer les packages pour la page actuelle
  const currentPackages = packages.slice((currentPage - 1) * packagesPerPage, currentPage * packagesPerPage);
  
  return (
    <div>
      {packages.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl shadow">
          <SearchIcon className="h-16 w-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-medium text-gray-700 mb-2">Aucun package ne correspond à vos critères</h3>
          <p className="text-gray-500">Essayez de modifier vos filtres pour trouver des packages</p>
        </div>
      ) : (
        <>
          <div className={displayMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 gap-6' : 'space-y-4'}>
            {currentPackages.map(pkg => (
              <PackageCard 
                key={pkg.id}
                package={pkg}
                isSelected={selectedItems.some(item => item.id === pkg.id && item.type === 'package')}
                onToggleSelection={() => onToggleSelection(pkg.id)}
                displayMode={displayMode}
              />
            ))}
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination 
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
            />
          )}
        </>
      )}
    </div>
  );
};

// PackageCard - Carte individuelle pour chaque package
const PackageCard = ({ package: pkg, isSelected, onToggleSelection, displayMode }) => {
  return (
    <motion.div
      className={`bg-white shadow rounded-xl overflow-hidden border ${isSelected ? `border-[${pkg.color}]` : 'border-transparent'} transition-all`}
      whileHover={{ y: -5, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)" }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      {displayMode === 'grid' ? (
        // Affichage en grille
        <>
          <div className="relative h-48">
            <img 
              src={pkg.imageUrl || "https://via.placeholder.com/400x200"} 
              alt={pkg.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0" style={{ backgroundColor: `${pkg.color}20` }}></div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
            
            {/* Badge de disponibilité */}
            <div className="absolute top-4 right-4">
              {pkg.availability ? (
                <span className="bg-green-500 text-white text-xs py-1 px-3 rounded-full flex items-center">
                  <CheckCircleIcon className="h-3 w-3 mr-1" />
                  Disponible
                </span>
              ) : (
                <span className="bg-red-500 text-white text-xs py-1 px-3 rounded-full flex items-center">
                  <XCircleIcon className="h-3 w-3 mr-1" />
                  Complet
                </span>
              )}
            </div>
            
            {/* Badge Featured si applicable */}
            {pkg.featured && (
              <div className="absolute top-4 left-4">
                <span className="bg-white text-[#E84E0E] text-xs py-1 px-3 rounded-full font-medium">
                  Recommandé
                </span>
              </div>
            )}
            
            {/* Nom et sous-titre */}
            <div className="absolute bottom-0 left-0 p-4 w-full">
              <h3 className="text-xl font-bold text-white mb-1">{pkg.name}</h3>
              <p className="text-white/80 text-sm">{pkg.subtitle}</p>
            </div>
          </div>
          
          <div className="p-4">
            <div className="mb-4" style={{ color: pkg.color }}>
              <span className="text-2xl font-bold">{pkg.price.toLocaleString()}</span>
              <span className="text-sm ml-1">FCFA</span>
              <span className="text-sm text-gray-500 ml-2">pour {pkg.duration}</span>
            </div>
            
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-gray-50 p-2 rounded text-center">
                <span className="block text-xs text-gray-500">Sites</span>
                <span className="text-lg font-bold text-gray-800">{pkg.siteCount}</span>
              </div>
              <div className="bg-gray-50 p-2 rounded text-center">
                <span className="block text-xs text-gray-500">Écrans</span>
                <span className="text-lg font-bold text-gray-800">{pkg.screenCount}</span>
              </div>
              <div className="bg-gray-50 p-2 rounded text-center">
                <span className="block text-xs text-gray-500">Vues</span>
                <span className="text-lg font-bold text-gray-800">{Math.round(pkg.estimatedViews/1000)}K</span>
              </div>
            </div>
            
            <div className="mb-4">
              <p className="text-gray-600 text-sm line-clamp-2">{pkg.description}</p>
            </div>
            
            <div className="flex justify-between items-center">
              <button 
                className="text-gray-600 hover:text-[#E84E0E] transition-colors flex items-center"
                onClick={() => {}} // Pour afficher les détails
              >
                <EyeIcon className="h-4 w-4 mr-1" />
                <span className="text-sm">Détails</span>
              </button>
              
              <motion.button
                className={`flex items-center justify-center p-2 rounded-lg ${
                  isSelected 
                    ? `bg-[${pkg.color}] text-white` 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                style={{ backgroundColor: isSelected ? pkg.color : '' }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onToggleSelection}
              >
                {isSelected ? (
                  <CheckCircleIcon className="h-5 w-5" />
                ) : (
                  <PlusCircleIcon className="h-5 w-5" />
                )}
              </motion.button>
            </div>
          </div>
        </>
      ) : (
        // Affichage en liste
        <div className="flex flex-col md:flex-row">
          <div className="relative md:w-1/3 h-40 md:h-auto">
            <img 
              src={pkg.imageUrl || "https://via.placeholder.com/400x200"} 
              alt={pkg.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0" style={{ backgroundColor: `${pkg.color}20` }}></div>
            <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent md:bg-gradient-to-t md:from-black/70 md:via-black/30 md:to-transparent"></div>
            
            {/* Badge Featured si applicable */}
            {pkg.featured && (
              <div className="absolute top-4 left-4">
                <span className="bg-white text-[#E84E0E] text-xs py-1 px-3 rounded-full font-medium">
                  Recommandé
                </span>
              </div>
            )}
            
            {/* Badge de disponibilité */}
            <div className="absolute top-4 right-4">
              {pkg.availability ? (
                <span className="bg-green-500 text-white text-xs py-1 px-3 rounded-full flex items-center">
                  <CheckCircleIcon className="h-3 w-3 mr-1" />
                  Disponible
                </span>
              ) : (
                <span className="bg-red-500 text-white text-xs py-1 px-3 rounded-full flex items-center">
                  <XCircleIcon className="h-3 w-3 mr-1" />
                  Complet
                </span>
              )}
            </div>
          </div>
          
          <div className="p-4 md:w-2/3 flex flex-col justify-between">
            <div>
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="text-xl font-bold text-gray-800">{pkg.name}</h3>
                  <p className="text-gray-600">{pkg.subtitle}</p>
                </div>
                <div style={{ color: pkg.color }}>
                  <span className="text-2xl font-bold">{pkg.price.toLocaleString()}</span>
                  <span className="text-sm ml-1">FCFA</span>
                  <div className="text-sm text-gray-500 text-right">{pkg.duration}</div>
                </div>
              </div>
              
              <p className="text-gray-600 text-sm mb-3 line-clamp-2">{pkg.description}</p>
              
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="flex items-center">
                  <div className="bg-gray-100 p-2 rounded-full mr-2">
                    <LocationMarkerIcon className="h-4 w-4 text-gray-600" />
                  </div>
                  <div>
                    <span className="block text-xs text-gray-500">Sites</span>
                    <span className="text-sm font-medium text-gray-800">{pkg.siteCount} sites</span>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="bg-gray-100 p-2 rounded-full mr-2">
                    <DesktopComputerIcon className="h-4 w-4 text-gray-600" />
                  </div>
                  <div>
                    <span className="block text-xs text-gray-500">Écrans</span>
                    <span className="text-sm font-medium text-gray-800">{pkg.screenCount} écrans</span>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="bg-gray-100 p-2 rounded-full mr-2">
                    <ChartBarIcon className="h-4 w-4 text-gray-600" />
                  </div>
                  <div>
                    <span className="block text-xs text-gray-500">Audience</span>
                    <span className="text-sm font-medium text-gray-800">{pkg.estimatedViews.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2 mb-3">
              {pkg.locations.slice(0, 3).map((location, index) => (
                <span 
                  key={index}
                  className="text-xs py-1 px-2 rounded-full"
                  style={{ backgroundColor: pkg.lightColor, color: pkg.color }}
                >
                  {location}
                </span>
              ))}
              {pkg.locations.length > 3 && (
                <span className="text-xs py-1 px-2 rounded-full bg-gray-100 text-gray-600">
                  +{pkg.locations.length - 3}
                </span>
              )}
            </div>
            
            <div className="flex justify-between items-center mt-2">
              <button 
                className="text-gray-600 hover:text-[#E84E0E] transition-colors flex items-center"
                onClick={() => {}} // Pour afficher les détails
              >
                <EyeIcon className="h-4 w-4 mr-1" />
                <span className="text-sm">Voir détails</span>
              </button>
              
              <motion.button
                className={`flex items-center justify-center p-2 rounded-lg ${
                  isSelected 
                    ? 'text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                style={{ backgroundColor: isSelected ? pkg.color : '' }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onToggleSelection}
              >
                {isSelected ? (
                  <span className="flex items-center">
                    <CheckCircleIcon className="h-5 w-5 mr-1" />
                    Sélectionné
                  </span>
                ) : (
                  <span className="flex items-center">
                    <PlusCircleIcon className="h-5 w-5 mr-1" />
                    Sélectionner
                  </span>
                )}
              </motion.button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

// CampaignPeriodSelector - Sélecteur de période pour la campagne
const CampaignPeriodSelector = ({ startDate, endDate, onPeriodChange }) => {
  // Formater les dates pour l'affichage
  const formatDate = (date) => {
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };
  
  return (
    <div className="bg-white rounded-xl p-4 border border-gray-200">
      <h3 className="font-medium text-gray-800 mb-3 flex items-center">
        <CalendarIcon className="h-5 w-5 mr-2 text-[#E84E0E]" />
        Période de campagne
      </h3>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-600 mb-1">Date de début</label>
          <input 
            type="date"
            className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E84E0E] focus:border-transparent"
            value={startDate.toISOString().split('T')[0]}
            min={new Date().toISOString().split('T')[0]}
            onChange={(e) => onPeriodChange('start', new Date(e.target.value))}
          />
          <div className="mt-1 text-sm text-gray-500">{formatDate(startDate)}</div>
        </div>
        
        <div>
          <label className="block text-sm text-gray-600 mb-1">Date de fin</label>
          <input 
            type="date"
            className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E84E0E] focus:border-transparent"
            value={endDate.toISOString().split('T')[0]}
            min={startDate.toISOString().split('T')[0]}
            onChange={(e) => onPeriodChange('end', new Date(e.target.value))}
          />
          <div className="mt-1 text-sm text-gray-500">{formatDate(endDate)}</div>
        </div>
      </div>
      
      <div className="mt-3 text-center text-sm text-gray-600">
        Durée : <span className="font-medium">{Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24))}</span> jours
      </div>
    </div>
  );
};

// CostEstimator - Calculateur automatique des coûts
const CostEstimator = ({ selectedItems, campaignPeriod, totalCost }) => {
  return (
    <div className="bg-white rounded-xl p-4 border border-gray-200">
      <h3 className="font-medium text-gray-800 mb-3 flex items-center">
        <CurrencyDollarIcon className="h-5 w-5 mr-2 text-[#E84E0E]" />
        Estimation des coûts
      </h3>
      
      <div className="mb-2">
        <div className="flex justify-between mb-1">
          <span className="text-gray-600">Éléments sélectionnés:</span>
          <span className="font-medium text-gray-800">{selectedItems.length}</span>
        </div>
        <div className="flex justify-between mb-1">
          <span className="text-gray-600">Durée de campagne:</span>
          <span className="font-medium text-gray-800">{Math.ceil((campaignPeriod.endDate - campaignPeriod.startDate) / (1000 * 60 * 60 * 24))} jours</span>
        </div>
      </div>
      
      <div className="border-t border-gray-200 pt-2 mt-2">
        <div className="flex justify-between items-baseline">
          <span className="text-gray-600">Coût total estimé:</span>
          <div>
            <span className="text-2xl font-bold text-[#E84E0E]">{totalCost.toLocaleString()}</span>
            <span className="text-gray-600 text-sm ml-1">FCFA</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// CartSummary - Résumé de la sélection/panier
const CartSummary = ({ 
  selectedItems, 
  onToggleSelection, 
  campaignPeriod, 
  onPeriodChange, 
  totalCost, 
  onClose,
  sitesData,
  packagesData
}) => {
  // Calculer la durée de la campagne en jours
  const campaignDuration = Math.ceil((campaignPeriod.endDate - campaignPeriod.startDate) / (1000 * 60 * 60 * 24));
  
  // Récupérer les détails complets des éléments sélectionnés
  const selectedSites = selectedItems
    .filter(item => item.type === 'site')
    .map(item => {
      const site = sitesData.find(s => s.id === item.id);
      return {
        ...site,
        subtotal: site.pricePerDay * campaignDuration
      };
    });
  
  const selectedPackages = selectedItems
    .filter(item => item.type === 'package')
    .map(item => {
      const pkg = packagesData.find(p => p.id === item.id);
      return pkg;
    });
  
  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-xl shadow-2xl overflow-hidden w-full max-w-4xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
      >
        {/* En-tête du panier */}
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-800 flex items-center">
            <ShoppingCartIcon className="h-6 w-6 mr-2 text-[#E84E0E]" />
            Votre sélection ({selectedItems.length})
          </h2>
          
          <button 
            className="p-2 rounded-full hover:bg-gray-200 text-gray-500"
            onClick={onClose}
          >
            <XIcon className="h-6 w-6" />
          </button>
        </div>
        
        {/* Contenu du panier */}
        <div className="flex-grow overflow-y-auto p-6">
          {selectedItems.length === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <ShoppingCartIcon className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-700 mb-2">Votre sélection est vide</h3>
              <p className="text-gray-500 mb-6">Ajoutez des sites ou des packages pour créer votre campagne</p>
              <button 
                className="px-6 py-2 bg-[#E84E0E] text-white rounded-lg hover:bg-[#d04305] transition-colors"
                onClick={onClose}
              >
                Parcourir les options
              </button>
            </div>
          ) : (
            <>
              {/* Période de campagne */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-800 mb-3">Période de campagne</h3>
                <CampaignPeriodSelector 
                  startDate={campaignPeriod.startDate}
                  endDate={campaignPeriod.endDate}
                  onPeriodChange={onPeriodChange}
                />
              </div>
              
              {/* Sites sélectionnés */}
              {selectedSites.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-800 mb-3">Sites sélectionnés</h3>
                  <div className="space-y-3">
                    {selectedSites.map(site => (
                      <div key={site.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex justify-between items-center">
                        <div className="flex items-center">
                          <div className="w-12 h-12 rounded-lg overflow-hidden mr-3">
                            <img 
                              src={site.imageUrl || "https://via.placeholder.com/100"}
                              alt={site.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-800">{site.name}</h4>
                            <div className="text-sm text-gray-500">{site.location} • {site.screens} écran(s)</div>
                            </div>
                        </div>
                        
                        <div className="flex items-center">
                          <div className="text-right mr-4">
                            <div className="text-gray-500 text-sm">{site.pricePerDay.toLocaleString()} FCFA × {campaignDuration} jours</div>
                            <div className="text-lg font-bold text-[#E84E0E]">{site.subtotal.toLocaleString()} FCFA</div>
                          </div>
                          
                          <button 
                            className="p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-red-500"
                            onClick={() => onToggleSelection(site.id, 'site')}
                          >
                            <XIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Packages sélectionnés */}
              {selectedPackages.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-800 mb-3">Packages sélectionnés</h3>
                  <div className="space-y-3">
                    {selectedPackages.map(pkg => (
                      <div key={pkg.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex justify-between items-center">
                        <div className="flex items-center">
                          <div 
                            className="w-12 h-12 rounded-lg overflow-hidden mr-3"
                            style={{ backgroundColor: `${pkg.lightColor}` }}
                          >
                            <img 
                              src={pkg.imageUrl || "https://via.placeholder.com/100"}
                              alt={pkg.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-800">{pkg.name}</h4>
                            <div className="text-sm text-gray-500">{pkg.subtitle} • {pkg.duration}</div>
                          </div>
                        </div>
                        
                        <div className="flex items-center">
                          <div className="text-right mr-4">
                            <div className="text-lg font-bold" style={{ color: pkg.color }}>{pkg.price.toLocaleString()} FCFA</div>
                          </div>
                          
                          <button 
                            className="p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-red-500"
                            onClick={() => onToggleSelection(pkg.id, 'package')}
                          >
                            <XIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Résumé des coûts */}
              <div className="bg-gray-50 rounded-xl p-6 mt-8">
                <h3 className="font-semibold text-gray-800 mb-4">Résumé des coûts</h3>
                
                <div className="space-y-3">
                  {selectedSites.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Sites ({selectedSites.length}):</span>
                      <span className="font-medium text-gray-800">
                        {selectedSites.reduce((sum, site) => sum + site.subtotal, 0).toLocaleString()} FCFA
                      </span>
                    </div>
                  )}
                  
                  {selectedPackages.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Packages ({selectedPackages.length}):</span>
                      <span className="font-medium text-gray-800">
                        {selectedPackages.reduce((sum, pkg) => sum + pkg.price, 0).toLocaleString()} FCFA
                      </span>
                    </div>
                  )}
                  
                  <div className="border-t border-gray-300 pt-3 mt-3">
                    <div className="flex justify-between items-baseline">
                      <span className="text-gray-700 font-medium">Total:</span>
                      <div>
                        <span className="text-2xl font-bold text-[#E84E0E]">{totalCost.toLocaleString()}</span>
                        <span className="text-gray-600 text-sm ml-1">FCFA</span>
                      </div>
                    </div>
                    <div className="text-sm text-gray-500 text-right mt-1">
                      TVA incluse
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* Pied de page avec boutons d'action */}
        {selectedItems.length > 0 && (
          <div className="bg-white px-6 py-4 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <button 
                className="text-gray-600 hover:text-gray-800 font-medium"
                onClick={onClose}
              >
                Continuer à explorer
              </button>
              
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="px-6 py-3 bg-[#E84E0E] text-white rounded-xl shadow-md hover:bg-[#d03a09] transition-colors flex items-center"
              >
                <span>Valider la commande</span>
                <ChevronRightIcon className="h-5 w-5 ml-2" />
              </motion.button>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

// Pagination - Navigation entre les pages de résultats
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  // Fonction pour générer un tableau de pages à afficher
  const getPageRange = () => {
    const range = [];
    const delta = 2; // Nombre de pages à afficher de chaque côté de la page courante
    
    for (let i = Math.max(1, currentPage - delta); i <= Math.min(totalPages, currentPage + delta); i++) {
      range.push(i);
    }
    
    return range;
  };
  
  const pageRange = getPageRange();
  
  return (
    <div className="flex justify-center mt-8">
      <div className="inline-flex items-center bg-white rounded-lg shadow-sm border border-gray-200">
        <button
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className={`p-3 border-r border-gray-200 rounded-l-lg ${
            currentPage === 1 
              ? 'text-gray-300 cursor-not-allowed' 
              : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          <ChevronLeftIcon className="h-5 w-5" />
        </button>
        
        {pageRange.map(page => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`w-10 py-3 border-r border-gray-200 ${
              page === currentPage 
                ? 'bg-[#E84E0E] text-white font-medium' 
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            {page}
          </button>
        ))}
        
        <button
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          className={`p-3 rounded-r-lg ${
            currentPage === totalPages 
              ? 'text-gray-300 cursor-not-allowed' 
              : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          <ChevronRightIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default DigitalNetworkPage;